import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow?: string
    title: ReactNode
    description?: ReactNode
    tone?: 'light' | 'dark'
    align?: 'left' | 'center'
    className?: string
    titleId?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    tone = 'light',
    align = 'left',
    className,
    titleId
}): ReactNode => {
    const dark = tone === 'dark'

    return (
        <div className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center', className)}>
            {eyebrow && (
                <p
                    className={cn(
                        'inline-flex items-center gap-3 text-xs font-bold tracking-[0.24em] uppercase',
                        dark ? 'text-gold' : 'text-crimson',
                        align === 'center' && 'justify-center'
                    )}
                >
                    <span aria-hidden='true' className={cn('h-px w-8', dark ? 'bg-gold/60' : 'bg-crimson/50')} />
                    {eyebrow}
                </p>
            )}

            <h2
                id={titleId}
                className={cn(
                    'mt-4 font-heading text-4xl leading-[1.05] tracking-tight text-balance sm:text-5xl lg:text-6xl',
                    dark ? 'text-white' : 'text-ink'
                )}
            >
                {title}
            </h2>

            {description && (
                <p className={cn('mt-5 text-base leading-relaxed sm:text-lg', dark ? 'text-white/70' : 'text-muted-foreground')}>
                    {description}
                </p>
            )}
        </div>
    )
}
